/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CompassOutlined,
  FileOutlined,
  LoadingOutlined,
  NotificationOutlined,
  SolutionOutlined,
  HomeOutlined,
  AppstoreOutlined,
  SettingOutlined,
  UserOutlined,
  CarOutlined,
  TruckOutlined,
  IdcardOutlined,
  ReconciliationOutlined,
  OrderedListOutlined,
} from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import { Badge, notification } from 'antd'

import { useContractContracts, useFilters, useGeneral, useRoutesFilters, useUser } from '@contexts'
import { Filters, PATHS, RoutesFilters, StatusTicketEnum, WebTitleEnum } from '@types'
import { useToAuthorize } from '@hooks'
import { ROUTE_KEYS } from '@constants'
import { ImplementationTimeUtils } from '@utils'

import {
  initialVehiclesFiltersState,
  useVehiclesContext,
} from '../../../contexts/VehiclesFiltersContext'
import { StyledMenu } from '../../../components/StyledComponents'
import { initialContractsFiltersState } from '../../../contexts/ContractsContext'
import {
  initialTrailersFiltersState,
  useTrailersContext,
} from '../../../contexts/TrailersFiltersContext'
import {
  initialCarriersFiltersState,
  useCarriersContext,
} from '../../../contexts/CarriersFiltersContext'

const AdminRoMenu: () => JSX.Element = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setFilters, resetFilters } = useFilters()
  const { state } = useUser()
  const gmtZone = state?.company?.gmtZone ?? 0
  const { setFilters: setContractFilters } = useContractContracts()
  const { setFilters: setVehiclesFilters } = useVehiclesContext()
  const { setFilters: setTrailersFilters } = useTrailersContext()
  const { setFilters: setCarriersFilters } = useCarriersContext()
  const { setFilters: setRoutesFilters } = useRoutesFilters()
  const { data, isLoading } = useToAuthorize()
  const { setTicketToSearch } = useGeneral()
  const [notified, setNotified] = useState(false)
  const [requests, setRequests] = useState(0)

  useEffect(() => {
    document.title = `${!data ? WebTitleEnum.TITLE : `${`(${data})`} ${WebTitleEnum.TITLE}`}`
  }, [data])

  useEffect(() => {
    if (!notified) {
      if (data) {
        notification.info({
          message:
            // eslint-disable-next-line no-nested-ternary
            data > 0
              ? data === 1
                ? `${t('notifications.preffix')} una ${t(
                    'globals.generics.to_authorize_request',
                  ).toLowerCase()}`
                : `${t('notifications.preffix')} ${data} ${t(
                    'globals.generics.to_authorize_requests',
                  ).toLowerCase()}`
              : `${t('preffix_not')} ${t('globals.generics.to_authorize_requests').toLowerCase()}`,
          duration: 3,
        })
        setNotified(true)
        setRequests(data)
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (data && data > requests && data - requests > 1) {
        notification.info({
          message: `Tiene ${data} solicitudes para autorizar`,
          duration: 3,
        })
        setRequests(data)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const clearFiltersOnComponentChange = () => {
    setTicketToSearch(undefined)
    resetFilters()
  }
  const handleCleanFilters = () => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [],
      carrierMode: [],
      statusName: [],
      supplierId: [],
      destinationId: [],
      orderBy: undefined,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const clearContractFiltersOnComponentChange = () => {
    setContractFilters(initialContractsFiltersState.filters)
  }

  const clearVehiclesFiltersOnComponentChange = () => {
    setVehiclesFilters(initialVehiclesFiltersState.filters)
  }
  const clearTrailersFiltersOnComponentChange = () => {
    setTrailersFilters(initialTrailersFiltersState.filters)
  }
  const clearCarriersFiltersOnComponentChange = () => {
    setCarriersFilters(initialCarriersFiltersState.filters)
  }
  const clearSuppliersFiltersOnComponentChange = () => {
    setCarriersFilters(initialCarriersFiltersState.filters)
  }

  const onOngoingClick = () => {
    navigate(ROUTE_KEYS.ADMINRO.HOME)
    handleCleanFilters()
    setFilters((prevState: Filters) => ({
      ...prevState,
      mondayOfWeek: ImplementationTimeUtils.companyStartOfIsoWeek(new Date(), gmtZone).toDate(),
    }))
    clearFiltersOnComponentChange()
  }

  const clearRoutesFiltersOnComponentChange = () => {
    setRoutesFilters((prevState: RoutesFilters) => ({
      ...prevState,
      page: 1,
      asnum: undefined,
      originCityCode: undefined,
      originPostCode: undefined,
      originZone: undefined,
      destinationCityCode: undefined,
      destinationPostCode: undefined,
      destinationZone: undefined,
      destinationRegion: undefined,
    }))
  }

  const onEVouchersClick = () => {
    navigate('/dashboard/adminro/evouchers')
    setFilters((prevState: Filters) => ({
      ...prevState,
      statusName: [
        StatusTicketEnum.CONFIRMED,
        StatusTicketEnum.TRANSIT,
        StatusTicketEnum.DELIVERED,
      ],
    }))
    clearFiltersOnComponentChange()
  }
  const onRoutesClick = () => {
    navigate('/dashboard/adminro/routes')
    clearRoutesFiltersOnComponentChange()
  }
  const onOrdersClick = () => {
    navigate('/dashboard/adminro/orders')
    clearFiltersOnComponentChange()
  }
  const onDriversClick = () => {
    navigate(PATHS.ADMINRO.DRIVERS)
    clearVehiclesFiltersOnComponentChange()
  }
  const onVehiclesClick = () => {
    navigate(PATHS.ADMINRO.VEHICLES)
    clearVehiclesFiltersOnComponentChange()
  }
  const onTrailersClick = () => {
    navigate(PATHS.ADMINRO.TRAILERS)
    clearTrailersFiltersOnComponentChange()
  }
  const onCarriersClick = () => {
    navigate(PATHS.ADMINRO.CARRIERS)
    clearCarriersFiltersOnComponentChange()
  }
  const onContractsClick = () => {
    navigate(PATHS.ADMINRO.CONTRACTS)
    clearContractFiltersOnComponentChange()
  }
  const onSuppliersClick = () => {
    navigate(PATHS.ADMINRO.SUPPLIERS)
    clearSuppliersFiltersOnComponentChange()
  }
  const onOpenBatchesClick = () => {
    navigate('/dashboard/adminro/open')
    clearFiltersOnComponentChange()
  }
  const onClosedBatchesClick = () => {
    navigate('/dashboard/adminro/closed')
    clearFiltersOnComponentChange()
  }
  const onCatertifiedClick = () => {
    navigate('/dashboard/adminro/certified')
    clearFiltersOnComponentChange()
  }
  const onSettingsClick = () => {
    navigate('/dashboard/adminro/settings')
    clearFiltersOnComponentChange()
  }
  const onSimClick = () => {
    navigate('/dashboard/adminro/simulation')
    clearFiltersOnComponentChange()
  }
  const onContractsClickSupplier = () => {
    navigate(PATHS.ADMINRO.CONTRACTS_SUPPLIERS)
    clearContractFiltersOnComponentChange()
  }
  const items = useMemo(
    () => [
      {
        key: PATHS.ADMINRO.ONGOING,
        onClick: onOngoingClick,
        icon: <HomeOutlined style={{ fontSize: '18px' }} />,
        label: (
          <>
            {t('admin_router.menu.requests')}
            {isLoading ? (
              <LoadingOutlined />
            ) : (
              <>
                {data && data > 0 ? (
                  <Badge className="menu-badge" count={data}>
                    <NotificationOutlined style={{ cursor: 'pointer' }} />
                  </Badge>
                ) : (
                  ''
                )}
              </>
            )}
          </>
        ),
      },
      {
        key: PATHS.ADMINRO.EVOUCHERS,
        onClick: onEVouchersClick,
        icon: <FileOutlined style={{ fontSize: '17px' }} />,
        label: t('tpl_router.menu.evouchers'),
      },
      {
        key: 'submen1',
        icon: <AppstoreOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.batches'),
        children: [
          {
            key: PATHS.ADMINRO.OPEN,
            onClick: onOpenBatchesClick,
            label: t('admin_router.menu.open'),
          },
          {
            key: PATHS.ADMINRO.CLOSED,
            onClick: onClosedBatchesClick,
            label: t('admin_router.menu.closed'),
          },
          {
            key: PATHS.ADMINRO.CERTIFIED,
            onClick: onCatertifiedClick,
            label: t('admin_router.menu.certified'),
          },
        ],
      },
      {
        key: PATHS.ADMINRO.ROUTES,
        onClick: onRoutesClick,
        icon: <CompassOutlined style={{ fontSize: '19px' }} />,
        label: t('admin_router.menu.routes'),
      },
      {
        key: 'submen2',
        icon: <IdcardOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.carriers'),
        children: [
          {
            key: PATHS.ADMINRO.CARRIERS,
            onClick: onCarriersClick,
            icon: (
              <span className="submenu-Ident">
                <IdcardOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.carriers'),
          },
          {
            key: PATHS.ADMINRO.DRIVERS,
            onClick: onDriversClick,
            icon: (
              <span className="submenu-Ident">
                <UserOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.drivers'),
          },
          {
            key: PATHS.ADMINRO.VEHICLES,
            onClick: onVehiclesClick,
            icon: (
              <span className="submenu-Ident">
                <CarOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.vehicles'),
          },
          {
            key: PATHS.ADMINRO.TRAILERS,
            onClick: onTrailersClick,
            icon: (
              <span className="submenu-Ident">
                <TruckOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.trailers'),
          },
          {
            key: PATHS.ADMINRO.CONTRACTS,
            onClick: onContractsClick,
            icon: (
              <span className="submenu-Ident">
                <SolutionOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: <span className="submenu">{t('admin_router.menu.contracts')}</span>,
          },
          {
            key: PATHS.ADMINRO.ORDERS,
            onClick: onOrdersClick,
            icon: (
              <span className="submenu-Ident">
                <OrderedListOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: <span className="submenu">{t('admin_router.menu.orders')}</span>,
          },
        ],
      },
      {
        key: 'submen3',
        icon: <ReconciliationOutlined style={{ fontSize: '18px' }} />,
        label: t('tpl_router.menu.supplier'),
        children: [
          {
            key: PATHS.ADMINRO.SUPPLIERS,
            onClick: onSuppliersClick,
            icon: (
              <span className="submenu-Ident">
                <ReconciliationOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('tpl_router.menu.supplier'),
          },
          {
            key: PATHS.ADMINRO.CONTRACTS,
            onClick: onContractsClickSupplier,
            icon: (
              <span className="submenu-Ident">
                <SolutionOutlined style={{ fontSize: '18px', marginLeft: '10px' }} />
              </span>
            ),
            label: <span className="submenu">{t('admin_router.menu.contracts')}</span>,
          },
        ],
      },
      {
        key: 'submen4',
        icon: <SettingOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.settings'),
        children: [
          {
            key: PATHS.ADMINRO.SETTINGS,
            onClick: onSettingsClick,
            label: t('admin_router.menu.general'),
          },
          {
            key: PATHS.ADMINRO.SIMULATION,
            onClick: onSimClick,
            label: t('admin_router.menu.simulation'),
          },
        ],
      },
    ],
    [
      pathname,
      onOngoingClick,
      t,
      isLoading,
      data,
      onEVouchersClick,
      onOpenBatchesClick,
      onClosedBatchesClick,
      onCatertifiedClick,
      onRoutesClick,
      onOrdersClick,
      onContractsClick,
      onSettingsClick,
      onSimClick,
      onCarriersClick,
      onDriversClick,
      onVehiclesClick,
      onTrailersClick,
    ],
  )
  return <StyledMenu mode="inline" selectedKeys={[pathname]} items={items} />
}

export { AdminRoMenu }
